

import '../index.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../../shared/AuthContext';
import rolePermissions from '../../../shared/rolePermissions';



import http from '../../../api/http';

const attachmentService = () => ({

  listRecordAttachments: async (resource, recordId) => {
    const response = await http.get(`/${resource}/${recordId}/attachments`);
    return response.data;
  },

  createRecordAttachment: async (resource, recordId, payload) => {
    const response = await http.post(`/${resource}/${recordId}/attachments`, payload);
    return response.data;
  },

  downloadRecordAttachment: async (attachmentId) => {
    const response = await http.get(`/attachments/${attachmentId}/download`);
    return response.data;
  },

  deleteRecordAttachment: async (attachmentId) => {
    await http.delete(`/attachments/${attachmentId}`);
  }

});



function formatBytes(sizeBytes) {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return '-';
  }
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toBase64Payload(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const encoded = result.includes(',') ? result.split(',')[1] : result;
      if (!encoded) {
        reject(new Error('Unable to process selected file.'));
        return;
      }
      resolve(encoded);
    };
    reader.onerror = () => reject(new Error('Unable to read selected file.'));
    reader.readAsDataURL(file);
  });
}

function buildDownloadBlob(contentBase64, mimeType) {
  const binary = window.atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}






const AttachmentsDomain = ({ slug, user, parentId }) => {

  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [workingAttachmentId, setWorkingAttachmentId] = useState('');

  const canViewContent = rolePermissions.hasPermission(user, 'content.view');
  const canManageAttachments = rolePermissions.hasPermission(user, 'attachments.manage');


  const loadAttachments = useCallback(async () => {
    if (!canViewContent) {
      setAttachments([]);
      return;
    }
    setAttachmentsLoading(true);
    setAttachmentsError('');
    try {

      const Factory = attachmentService();

      const response = await Factory.listRecordAttachments( slug, parentId );

      const rows = response.data || [];

      setAttachments(rows);
    } catch (err) {
      setAttachmentsError(`Attachments Error: ${err?.response?.data?.message || err.message || 'Unable to load attachments.'}`);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [ slug, parentId, canViewContent]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  async function handleAttachmentUpload(event) {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!canManageAttachments) {
      setAttachmentsError('You do not have permission to upload attachments.');
      return;
    }
    const formData = new FormData(formElement);
    const selectedFile = formData.get('file');
    if (!(selectedFile instanceof File) || !selectedFile.name) {
      setAttachmentsError('Please choose a file to upload.');
      return;
    }

    setUploadingAttachment(true);
    setAttachmentsError('');
    try {
      const contentBase64 = await toBase64Payload(selectedFile);

      const Factory = attachmentService();

      await Factory.createRecordAttachment( slug, parentId, {
        fileName: selectedFile.name,
        mimeType: selectedFile.type || 'application/octet-stream',
        contentBase64,
      } );

      formElement.reset();
      await loadAttachments();
    } catch (err) {
      setAttachmentsError(`Attachments Error: ${err?.response?.data?.message || err.message || 'Unable to upload attachment.'}`);
    } finally {
      setUploadingAttachment(false);
    }
  }

  async function handleAttachmentDelete(attachmentId) {
    if (!canManageAttachments) {
      setAttachmentsError('You do not have permission to delete attachments.');
      return;
    }
    setWorkingAttachmentId(attachmentId);
    setAttachmentsError('');
    try {

      const Factory = attachmentService();

      await Factory.deleteRecordAttachment( attachmentId );
      setAttachments((prev) => prev.filter((row) => row.id !== attachmentId));
    } catch (err) {
      setAttachmentsError(`Attachments Error: ${err?.response?.data?.message || err.message || 'Unable to delete attachment.'}`);
    } finally {
      setWorkingAttachmentId('');
    }
  }

  async function handleAttachmentDownload(attachmentId) {
    setWorkingAttachmentId(attachmentId);
    setAttachmentsError('');
    try {

      const Factory = attachmentService();

      const payload = await Factory.downloadRecordAttachment( attachmentId );
      const blob = buildDownloadBlob(payload.contentBase64, payload.mimeType);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = payload.fileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setAttachmentsError(`Attachments Error: ${err?.response?.data?.message || err.message || 'Unable to download attachment.'}`);
    } finally {
      setWorkingAttachmentId('');
    }
  }

  const RecordSection = require( '../../../components/view/RecordSection' ).default;

  return (
    <RecordSection title="Attachments">
      {attachmentsError ? <p className="error-text">{attachmentsError}</p> : null}

      {canManageAttachments ? (
        <form className="action-cluster" onSubmit={handleAttachmentUpload}>
          <input name="file" type="file" required disabled={uploadingAttachment} />
          <button className="button-primary" type="submit" disabled={uploadingAttachment}>
            {uploadingAttachment ? 'Uploading...' : 'Upload'}
          </button>
        </form>
      ) : (
        <p className="small-muted">You can view attachments but cannot upload or delete them.</p>
      )}

      {attachmentsLoading ? <p>Loading attachments...</p> : null}

      {!attachmentsLoading && !attachments.length ? (
        <p className="small-muted">No attachments yet.</p>
      ) : null}

      {!attachmentsLoading && attachments.length ? (
        <div>
          <table className="data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((attachment) => (
                <tr key={attachment.id}>
                  <td>{attachment.fileName}</td>
                  <td>{attachment.mimeType}</td>
                  <td>{formatBytes(attachment.sizeBytes)}</td>
                  <td>{attachment.createdAt ? new Date(attachment.createdAt).toLocaleString() : '-'}</td>
                  <td className="action-cluster">
                    <button
                      className="button-secondary"
                      type="button"
                      onClick={() => handleAttachmentDownload(attachment.id)}
                      disabled={workingAttachmentId === attachment.id}
                    >
                      {workingAttachmentId === attachment.id ? 'Working...' : 'Download'}
                    </button>
                    {canManageAttachments ? (
                      <button
                        className="button-danger"
                        type="button"
                        onClick={() => handleAttachmentDelete(attachment.id)}
                        disabled={workingAttachmentId === attachment.id}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </RecordSection>
  );
};






const Component = ({ model }) => {

  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const { user, loadingSession } = useAuth();
  const canViewContent = rolePermissions.hasPermission(user, 'content.view');
  const canUpdateContent = rolePermissions.hasPermission(user, 'content.update');


  const [record, setRecord] = useState(null);
  const [domain, setDomain] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  
  useEffect(() => {

    const loadRecord = async () => {
      setLoading(true);
      setError('');
      try {

        const Service = {

          getRecord: async ( id, setRecord ) => {

            const result = {};
            
            const Factory = require( '../../../records/modules' ).default;

            const row = await Factory( {
              slug: model.slug
            } ).Data.getRecord( id );

            result.data = row;

            await Promise.all(
              model.fields.map( async ( field ) => {

                const Components = require( '../../../elements' ).default;

                const Component = Components.Data.identity( field.field.value );

                await Component.loadData({
                  handle: field.handle,
                  meta: field.field.meta,
                  record: result.data,
                  result: result
                });
              } )
            );

            setRecord( result );
          },

          getDomain: async ( id, setDomain ) => {

            const result = {};

            await Promise.all(
              model.domain.map( async ( domain ) => {

                const Components = require( '../../../elements' ).default;

                const Component = Components.Data.domain( domain.domain.value );
                
                await Component.loadDomain({
                  handle: domain.handle,
                  meta: domain.domain.meta,
                  parentId: id,
                  result: result
                });
              } )
            );

            setDomain( result );
          },

        };

        await Service.getRecord(id, setRecord);
        await Service.getDomain(id, setDomain);
      } catch (err) {
        setError(`Record Error: ${err?.response?.data?.message || err.message || 'Unable to load Record.'}`);
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [ model, id, reloadKey ]);







  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [approvalOffer, setApprovalOffer] = useState(null);









  
  const RecordViewSection = require( '../../../components/record/RecordViewSection' ).default;



  if (loadingSession) return <p>Checking access...</p>;
  if (!canViewContent) return <p className="error-text">You do not have permission to view this content.</p>;

  if (loading) return <p>Loading Record...</p>;

  if (error) return <p className="error-text">{error}</p>;
  if (!record) return <p className="error-text">Record not found.</p>;

  const {
    handle,
    details
  } = {
    handle: {
      slug: model.slug,
      handle: model.handle,
      status: model.statusConfig,
      details: model.fields,
    },
    section: model.section,
    slug: model.slug,
    details: {
      items: model.domain
    }
  };

  return (
    <RecordViewSection
      slug={handle.slug}
      header={{}}
      canUpdateContent={canUpdateContent}
      data={{
        record: record,
        hasStatus: true,
        status: {
          value: record.data.status,
          options: handle.status.options,
          onSubmit: async (status) => {
            setStatusSaving(true);
            setStatusError('');
            try {

              const result = {};

              const payload = { status };
              
              const Factory = require( '../../../records/modules' ).default;

              const updated = await Factory( {
                slug: model.slug
              } ).Data.updateRecord( id, payload );

              result.data = updated;

              await Promise.all(
                model.fields.map( async ( field ) => {

                  const Components = require( '../../../elements' ).default;

                  const Component = Components.Data.identity( field.field.value );

                  await Component.loadData({
                    handle: field.handle,
                    meta: field.field.meta,
                    record: result.data,
                    result: result
                  });
                } )
              );

              setRecord( result );
            } catch (err) {
              setStatusError(`Status Error: ${err?.response?.data?.message || err.message || 'Unable to update Record status.'}`);
              const approvalRequired = err?.response?.data?.approvalRequired;
              setApprovalOffer(approvalRequired ? { to: status, ...approvalRequired } : null);
            } finally {
              setStatusSaving(false);
            }
          },
          saving: statusSaving,
          error: statusError,
          approval: {
            offer: approvalOffer,
            onOfferHandled: () => {
              setApprovalOffer(null);
              setStatusError('');
            },
            onApplied: () => setReloadKey((key) => key + 1),
          },
        },
        handle: {
          handle: handle.handle.handle,
          label: handle.handle.label,
          meta: handle.handle.field.meta,
          value: handle.handle.field.value,
        },
        details: handle.details.map( ( field ) => ({
          handle: field.handle,
          label: field.label,
          meta: field.field.meta,
          value: field.field.value,
        }) )
      }}
    >


      {details.items.map( ( detial, index ) => {

        const Components = require( '../../../elements' ).default;

        const Component = Components.Data.domain( detial.domain.value );

        return <Component.Output handle={detial.handle} meta={detial.domain.meta} domain={domain} parentId={id} />;
      })}

      <AttachmentsDomain slug={model.slug} user={user} parentId={id} />

    </RecordViewSection>
  );
};






export default Component;